/**
 * IntroductionManager Component
 *
 * Admin UI for managing exam module introductions, including CRUD operations,
 * file uploads (image/audio), filtering, pagination, and detailed viewing.
 * Communicates with the backend via authenticated REST endpoints.
 *
 * @module IntroductionManager
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  FileImage,
  FileAudio,
  Eye,
  Loader2,
} from 'lucide-react';
import {
  convertFileToDataURL,
  extractBase64FromDataURL,
} from '@/lib/fileUtils';
import {
  MODULES,
  EXAM_TYPES,
  VALID_IMAGE_TYPES,
  VALID_AUDIO_TYPES,
  CEFR_LEVELS,
} from '@/lib/constants';
import { ExamTypeMultiSelect } from '@/components/ui/exam-type-multi-select';
import {
  type Introduction,
  getIntroductionsList,
  getIntroductionFull,
  createIntroduction,
  updateIntroduction,
  deleteIntroduction,
} from '@/lib/introductionApi';
import { logger } from '@/lib/logger';

/**
 * Local form state for creating/updating an introduction.
 */
interface IntroductionFormData {
  title: string;
  subtitle: string;
  text: string;
  material_type: 'text' | 'image' | 'audio' | null;
  duration: number | null;
  module: 'reading' | 'listening' | 'writing' | 'speaking';
  exam_type: string[];
  level: string;
  file?: File;
}

export default function IntroductionManager() {
  const [introductions, setIntroductions] = useState<Introduction[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [examTypeFilter, setExamTypeFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingIntro, setEditingIntro] = useState<Introduction | null>(null);
  const [viewingIntro, setViewingIntro] = useState<Introduction | null>(null);
  const [deletingIntro, setDeletingIntro] = useState<Introduction | null>(null);
  const [formData, setFormData] = useState<IntroductionFormData>({
    title: '',
    subtitle: '',
    text: '',
    material_type: null,
    duration: null,
    module: 'reading',
    exam_type: ['goethe'],
    level: null,
  });
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const introsPerPage = 10;

  // Use constants from lib/constants
  const modules = MODULES;
  const examTypes = EXAM_TYPES;

  // Load introductions on component mount
  useEffect(() => {
    loadIntroductions();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, moduleFilter, examTypeFilter, levelFilter]);

  /**
   * Fetch lightweight list of introductions from the backend with auth.
   * Updates local `introductions` state and handles loading/errors.
   */
  const loadIntroductions = async () => {
    setLoading(true);
    try {
      const data = await getIntroductionsList();
      setIntroductions(data);
    } catch (error) {
      logger.error('Error loading introductions:', error);
      toast.error('Failed to load introductions');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle file selection for image/audio materials, including basic validation
   * (size/type) and generating a data URL preview for submission.
   *
   * @param file The selected file or null to clear.
   */
  const handleFileSelect = (file: File | null) => {
    if (!file) {
      setFilePreview(null);
      setFormData((prev) => ({ ...prev, file: undefined }));
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Validate file type based on material_type
    const fileType = file.type;

    if (
      formData.material_type === 'image' &&
      !VALID_IMAGE_TYPES.includes(fileType as any)
    ) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    if (
      formData.material_type === 'audio' &&
      !VALID_AUDIO_TYPES.includes(fileType as any)
    ) {
      toast.error('Please select a valid audio file (MP3, WAV, or WebM)');
      return;
    }

    setFormData((prev) => ({ ...prev, file }));

    // Create preview using utility
    convertFileToDataURL(file)
      .then(setFilePreview)
      .catch((error) => {
        logger.error('Error creating preview:', error);
        toast.error('Failed to create file preview');
      });
  };

  /**
   * Reset form inputs and preview to their initial state.
   */
  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      text: '',
      material_type: null,
      duration: null,
      module: 'reading',
      exam_type: ['goethe'],
      level: 'A1',
    });
    setFilePreview(null);
  };

  /**
   * Open the edit dialog for a specific introduction and preload form state.
   *
   * @param intro The introduction to edit.
   */
  const openEditDialog = (intro: Introduction) => {
    setEditingIntro(intro);
    setFormData({
      title: intro.title,
      subtitle: intro.subtitle || '',
      text: intro.text || '',
      material_type: intro.material_type,
      duration: intro.duration,
      module: intro.module,
      exam_type: intro.exam_type,
      level: intro.level || 'A1',
    });
    // Set preview if material exists
    if (intro.material_binary && intro.material_mime_type) {
      setFilePreview(
        `data:${intro.material_mime_type};base64,${intro.material_binary}`,
      );
    } else {
      setFilePreview(null);
    }
  };

  /**
   * Open the view dialog for the selected introduction.
   * Loads full content including media when preview is opened.
   *
   * @param intro The introduction to view (lightweight from list).
   */
  const openViewDialog = async (intro: Introduction) => {
    setViewingIntro(intro);
    setShowViewDialog(true);

    // Load full content including media when preview opens
    try {
      const fullIntro = await getIntroductionFull(intro.id);
      setViewingIntro(fullIntro);
    } catch (error) {
      logger.error('Error loading full introduction:', error);
      toast.error('Failed to load introduction details');
    }
  };

  /**
   * Open the delete confirmation dialog for the selected introduction.
   *
   * @param intro The introduction to delete.
   */
  const openDeleteDialog = (intro: Introduction) => {
    setDeletingIntro(intro);
    setShowDeleteDialog(true);
  };

  /**
   * Create a new introduction via POST. Handles optional file upload by
   * sending base64 content and metadata when provided.
   */
  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      // Prepare the request body
      const body: any = {
        title: formData.title,
        subtitle: formData.subtitle || '',
        text: formData.text || '',
        module: formData.module,
        exam_type: formData.exam_type,
        level: formData.level,
      };

      if (formData.material_type) {
        body.material_type = formData.material_type;
      }
      if (formData.duration) {
        body.duration = formData.duration;
      }

      // If file is selected, convert to base64
      if (formData.file && filePreview) {
        // Extract base64 using utility
        body.material_base64 = extractBase64FromDataURL(filePreview);
        body.material_extension = formData.file.name.split('.').pop();
        body.material_mime_type = formData.file.type;
      }

      await createIntroduction(body);
      toast.success('Introduction created successfully!');
      setShowCreateDialog(false);
      resetForm();
      loadIntroductions();
    } catch (error: any) {
      logger.error('Error creating introduction:', error);
      toast.error(`Failed to create introduction: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Update an existing introduction via PUT. Supports changing metadata and
   * replacing/adding material via base64 file data when a new file is selected.
   */
  const handleUpdate = async () => {
    if (!editingIntro || !formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      // Prepare the request body
      const body: any = {
        title: formData.title,
        subtitle: formData.subtitle || '',
        text: formData.text || '',
        module: formData.module,
        exam_type: formData.exam_type,
        level: formData.level,
      };

      if (formData.material_type !== undefined) {
        body.material_type = formData.material_type;
      }
      if (formData.duration !== undefined) {
        body.duration = formData.duration;
      }

      // If file is selected, convert to base64
      if (formData.file && filePreview) {
        // Extract base64 using utility
        body.material_base64 = extractBase64FromDataURL(filePreview);
        body.material_extension = formData.file.name.split('.').pop();
        body.material_mime_type = formData.file.type;
      }

      await updateIntroduction(editingIntro.id, body);
      toast.success('Introduction updated successfully!');
      setEditingIntro(null);
      resetForm();
      loadIntroductions();
    } catch (error: any) {
      logger.error('Error updating introduction:', error);
      toast.error(`Failed to update introduction: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Delete the selected introduction via DELETE and refresh the list.
   */
  const handleDelete = async () => {
    if (!deletingIntro) return;

    try {
      await deleteIntroduction(deletingIntro.id);
      toast.success('Introduction deleted successfully!');
      setShowDeleteDialog(false);
      setDeletingIntro(null);
      loadIntroductions();
    } catch (error: any) {
      logger.error('Error deleting introduction:', error);
      toast.error(`Failed to delete introduction: ${error.message}`);
    }
  };

  const filteredIntroductions = introductions.filter((intro) => {
    const matchesSearch =
      intro.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intro.subtitle?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule =
      moduleFilter === 'all' || intro.module === moduleFilter;
    const matchesExamType =
      examTypeFilter === 'all' || intro.exam_type.includes(examTypeFilter);
    const matchesLevel = levelFilter === 'all' || intro.level === levelFilter;

    return matchesSearch && matchesModule && matchesExamType && matchesLevel;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredIntroductions.length / introsPerPage);
  const startIndex = (currentPage - 1) * introsPerPage;
  const endIndex = startIndex + introsPerPage;
  const currentIntroductions = filteredIntroductions.slice(
    startIndex,
    endIndex,
  );

  return (
    <div className="space-y-6">
      {/* Introduction Form Modal */}
      <Dialog
        open={!!(showCreateDialog || editingIntro)}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingIntro(null);
            resetForm();
          }
        }}
      >
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-ui">
              {editingIntro ? 'Edit Introduction' : 'Create Introduction'}
            </DialogTitle>
            <DialogDescription className="font-ui">
              {editingIntro
                ? 'Update the introduction details'
                : 'Create a new introduction'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title" className="font-ui">
                Title *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Introduction title"
              />
            </div>

            <div>
              <Label htmlFor="subtitle" className="font-ui">
                Subtitle
              </Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subtitle: e.target.value }))
                }
                placeholder="Optional subtitle"
              />
            </div>

            <div>
              <Label htmlFor="text" className="font-ui">
                Text Content
              </Label>
              <Textarea
                id="text"
                value={formData.text}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, text: e.target.value }))
                }
                placeholder="Introduction text content"
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="module" className="font-ui">
                Module *
              </Label>
              <Select
                value={formData.module}
                onValueChange={(
                  value: 'reading' | 'listening' | 'writing' | 'speaking',
                ) => setFormData((prev) => ({ ...prev, module: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((module) => (
                    <SelectItem key={module.value} value={module.value}>
                      <span className="font-ui">{module.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <ExamTypeMultiSelect
                label="Exam Type *"
                selectedTypes={formData.exam_type}
                onChange={(updatedTypes) =>
                  setFormData((prev) => ({ ...prev, exam_type: updatedTypes }))
                }
                htmlId="exam-type"
              />
            </div>

            <div>
              <Label htmlFor="level" className="font-ui">
                Level (CEFR) *
              </Label>
              <Select
                value={formData.level}
                onValueChange={(value) => {
                  setFormData((prev) => ({ ...prev, level: value }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {CEFR_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      <span className="font-ui">{level}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="material-type" className="font-ui">
                  Material Type
                </Label>
                <Select
                  value={formData.material_type || 'none'}
                  onValueChange={(value) => {
                    const newType =
                      value === 'none' ? null : (value as 'image' | 'audio');
                    setFormData((prev) => ({
                      ...prev,
                      material_type: newType,
                    }));
                    if (newType === null) {
                      setFilePreview(null);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="font-ui">None</span>
                    </SelectItem>
                    <SelectItem value="image">
                      <span className="font-ui">Image</span>
                    </SelectItem>
                    <SelectItem value="audio">
                      <span className="font-ui">Audio</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="duration" className="font-ui">
                  Duration (seconds)
                </Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: e.target.value
                        ? Number.parseInt(e.target.value)
                        : null,
                    }))
                  }
                  placeholder="Optional"
                />
              </div>
            </div>

            {formData.material_type && (
              <div>
                <Label htmlFor="material-file" className="font-ui">
                  Upload{' '}
                  {formData.material_type.charAt(0).toUpperCase() +
                    formData.material_type.slice(1)}
                </Label>
                <div className="mt-2">
                  <Input
                    id="material-file"
                    type="file"
                    accept={
                      formData.material_type === 'image' ? 'image/*' : 'audio/*'
                    }
                    onChange={(e) =>
                      handleFileSelect(e.target.files?.[0] || null)
                    }
                  />
                </div>
                {filePreview && (
                  <div className="mt-4 relative max-w-xs">
                    {formData.material_type === 'image' ? (
                      <img
                        src={filePreview}
                        alt="Preview"
                        className="rounded-lg max-h-48"
                      />
                    ) : (
                      <audio
                        src={filePreview}
                        controls
                        className="w-full"
                      />
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setFilePreview(null);
                        setFormData((prev) => ({ ...prev, file: undefined }));
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  setEditingIntro(null);
                  resetForm();
                }}
              >
                <span className="font-ui">Cancel</span>
              </Button>
              <Button
                onClick={editingIntro ? handleUpdate : handleCreate}
                disabled={saving}
              >
                {saving && (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="font-ui">Saving...</span>
                  </>
                )}
                {!saving && editingIntro && (
                  <span className="font-ui">Save Changes</span>
                )}
                {!saving && !editingIntro && (
                  <span className="font-ui">Create Introduction</span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Introduction Management Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-ui">Introduction Management</CardTitle>
              <CardDescription className="font-ui">
                Manage exam module introductions
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="font-ui">Create Introduction</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search introductions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Modules</span>
                </SelectItem>
                {modules.map((module) => (
                  <SelectItem key={module.value} value={module.value}>
                    <span className="font-ui">{module.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={examTypeFilter} onValueChange={setExamTypeFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Exam Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Exam Types</span>
                </SelectItem>
                {examTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="font-ui">{type.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Levels</span>
                </SelectItem>
                {CEFR_LEVELS.map((level) => (
                  <SelectItem key={level} value={level}>
                    <span className="font-ui">{level}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Introductions Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-ui">Title</TableHead>
                  <TableHead className="font-ui">Subtitle</TableHead>
                  <TableHead className="font-ui">Module</TableHead>
                  <TableHead className="font-ui">Exam Type</TableHead>
                  <TableHead className="font-ui">Level</TableHead>
                  <TableHead className="font-ui">Material</TableHead>
                  <TableHead className="font-ui">Duration</TableHead>
                  <TableHead className="w-20 font-ui">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="font-ui">Loading introductions...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredIntroductions.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      <span className="font-ui">No introductions found</span>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredIntroductions.length > 0 && (
                  currentIntroductions.map((intro) => (
                    <TableRow key={intro.id}>
                      <TableCell className="font-ui font-medium">
                        {intro.title}
                      </TableCell>
                      <TableCell className="font-ui">
                        {intro.subtitle || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-ui">
                          {intro.module}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {intro.exam_type.map((type) => (
                            <Badge
                              key={`${intro.id}-${type}`}
                              variant="secondary"
                              className="text-xs font-ui"
                            >
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-ui">
                          {intro.level}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {intro.material_type && (
                          <Badge
                            variant={
                              intro.material_type === 'image'
                                ? 'default'
                                : 'secondary'
                            }
                            className="font-ui"
                          >
                            {intro.material_type === 'image' && (
                              <FileImage className="h-3 w-3 mr-1" />
                            )}
                            {intro.material_type === 'audio' && (
                              <FileAudio className="h-3 w-3 mr-1" />
                            )}
                            {intro.material_type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-ui">
                        {intro.duration ? `${intro.duration}s` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openViewDialog(intro)}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(intro)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(intro)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {filteredIntroductions.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="text-sm text-muted-foreground font-ui">
                Showing {currentIntroductions.length} of{' '}
                {filteredIntroductions.length} introductions (Page {currentPage}{' '}
                of {totalPages})
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                  >
                    <span className="font-ui">Previous</span>
                  </Button>
                  <span className="text-sm text-muted-foreground px-2 font-ui">
                    {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    <span className="font-ui">Next</span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-ui">{viewingIntro?.title}</DialogTitle>
            <DialogDescription className="font-ui">
              View introduction details
            </DialogDescription>
          </DialogHeader>

          {viewingIntro && (
            <div className="space-y-4">
              <div>
                <Label className="font-ui">Subtitle</Label>
                <p className="text-sm font-ui">
                  {viewingIntro.subtitle || '-'}
                </p>
              </div>
              <div>
                <Label className="font-ui">Text Content</Label>
                <p className="text-sm font-ui">{viewingIntro.text || '-'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-ui">Module</Label>
                  <p className="text-sm font-ui">{viewingIntro.module}</p>
                </div>
                <div>
                  <Label className="font-ui">Exam Type</Label>
                  <p className="text-sm font-ui">
                    {viewingIntro.exam_type.join(', ')}
                  </p>
                </div>
                <div>
                  <Label className="font-ui">Level</Label>
                  <p className="text-sm font-ui">{viewingIntro.level}</p>
                </div>
                <div>
                  <Label className="font-ui">Duration</Label>
                  <p className="text-sm font-ui">
                    {viewingIntro.duration
                      ? `${viewingIntro.duration}s`
                      : '-'}
                  </p>
                </div>
              </div>
              {viewingIntro.material_type &&
                (viewingIntro.material_url || viewingIntro.material_binary) && (
                  <div>
                    <Label className="font-ui">
                      Material ({viewingIntro.material_type})
                    </Label>
                    <div className="mt-2">
                      {(() => {
                        if (viewingIntro.material_type === 'image') {
                          return (
                            <img
                              src={`data:${viewingIntro.material_mime_type};base64,${viewingIntro.material_binary}`}
                              alt="Material"
                              className="rounded-lg max-h-64"
                            />
                          );
                        }
                        if (viewingIntro.material_type === 'audio') {
                          return (
                            <section className="p-4 bg-muted rounded-lg">
                              <audio
                                src={
                                  viewingIntro.material_url ||
                                  (viewingIntro.material_binary
                                    ? `data:${viewingIntro.material_mime_type || 'audio/mpeg'};base64,${viewingIntro.material_binary}`
                                    : undefined)
                                }
                                controls
                                className="w-full"
                                preload="metadata"
                                aria-label="Audio content"
                              >
                                <track kind="captions" src="" srcLang="en" label="English" />
                                Your browser does not support the audio element.
                              </audio>
                              {viewingIntro.duration ? (
                                <p className="text-xs text-muted-foreground mt-2 font-ui">
                                  Duration: {viewingIntro.duration}s
                                </p>
                              ) : null}
                            </section>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowViewDialog(false)}
            >
              <span className="font-ui">Close</span>
            </Button>
            {viewingIntro && (
              <Button
                onClick={() => {
                  setShowViewDialog(false);
                  openEditDialog(viewingIntro);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                <span className="font-ui">Edit</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">
              Delete Introduction
            </AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              Are you sure you want to delete "{deletingIntro?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">Delete</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
